import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CalcConfigService } from '_services/calc-config.service';
import { Population, Block } from '../../_models/population';
import { combineLatest, Subscription } from 'rxjs';
import { filter as lodashFilter } from 'lodash-es';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

import { AidYearComponent } from '../../_shared/aid-year/aid-year.component';
import { PopulationComponent } from '../population/population.component';

@Component({
    selector: 'app-population-detail',
    templateUrl: './population-detail.component.html',
    styleUrls: ['./population-detail.component.css'],
    imports: [MatButton, MatIcon, AidYearComponent, PopulationComponent]
})
export class PopulationDetailComponent implements OnInit, OnDestroy {
  population: Population | null = null;
  populationCopy: Population | null = null;
  editMode = false;
  isNew = false;
  notFound = false;
  loading = true;

  private subscription?: Subscription;
  private schoolId = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public cc: CalcConfigService
  ) {}

  ngOnInit(): void {
    this.schoolId = this.route.parent?.parent?.snapshot.paramMap.get('schoolId') ?? '';
    this.subscription = combineLatest([this.route.paramMap, this.cc.populations]).subscribe(([params, populations]) => {
      this.resolvePopulation(params, populations);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  startEdit(): void {
    if (!this.population) {
      return;
    }
    this.populationCopy = this.clonePopulation(this.population);
    this.editMode = true;
  }

  save(): void {
    if (!this.populationCopy) {
      return;
    }

    if (this.populationCopy.nextStep) {
      // Quill's HTML export encodes every space as &nbsp;, which will not wrap on the results page.
      this.populationCopy.nextStep = this.populationCopy.nextStep.replace(/&nbsp;/gi, ' ').replace(/\u00A0/g, ' ');
      if (this.populationCopy.nextStep.replace(/<[^>]*>?/gm, '').trim().length === 0) {
        this.populationCopy.nextStep = '';
      }
    }

    this.populationCopy.blocks.forEach(block => {
      block.conditions = block.conditions.filter(cond => cond.keyId || cond.formulaId);
      block.conditions = block.conditions.filter(cond => !!cond.value || cond.value === 0 || cond.values);
    });

    this.populationCopy.blocks = this.populationCopy.blocks.filter(block => block.conditions.length > 0);
    this.populationCopy.name = this.populationCopy.name === '' ? this.population?.name ?? '' : this.populationCopy.name;

    this.population = this.clonePopulation(this.populationCopy);
    if (this.populationCopy.blocks.length > 0) {
      this.cc.savePopulation(this.schoolId, this.populationCopy);
    } else {
      this.goBack();
      return;
    }

    this.populationCopy = this.clonePopulation(this.population);
    this.editMode = false;
    if (this.isNew) {
      this.router.navigate(['../', this.population.id], { relativeTo: this.route });
      this.isNew = false;
    }
  }

  cancel(): void {
    if (this.isNew) {
      this.router.navigate(['../'], { relativeTo: this.route });
      return;
    }
    if (this.population) {
      this.populationCopy = this.clonePopulation(this.population);
    }
    this.editMode = false;
  }

  confirmDelete(): void {
    if (!this.population) {
      return;
    }

    const usage = lodashFilter(this.cc.usedBy.value, { entityId: this.population.id, entityType: 'Population' });
    if (usage.length > 0) {
      alert('This population is being used. You need to remove all uses before deleting the population (including next steps)');
      return;
    }

    const confirmed = confirm(`Delete "${this.population.name}"?`);
    if (!confirmed) {
      return;
    }

    this.cc.deletePopulation(this.schoolId, this.population);
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  goBack(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  get activePopulationName(): string {
    if (this.editMode) {
      return this.populationCopy?.name || 'New Population';
    }
    return this.population?.name || 'New Population';
  }

  private resolvePopulation(params: ParamMap, populations: Population[]): void {
    const populationId = params.get('populationId');
    if (!populationId) {
      if (!this.isNew) {
        this.initializeNewPopulation();
      }
      this.loading = false;
      this.notFound = false;
      return;
    }

    this.isNew = false;
    const population = populations.find(p => p.id === populationId);
    if (!population) {
      this.population = null;
      this.populationCopy = null;
      this.notFound = true;
      this.loading = false;
      return;
    }

    this.notFound = false;
    this.population = population;
    if (!this.editMode) {
      this.populationCopy = this.clonePopulation(population);
    }
    this.loading = false;
  }

  private initializeNewPopulation(): void {
    const block: Block = {
      blockOperatorAnd: true,
      conditions: []
    };

    const newPopulation: Population = {
      id: this.cc.getUniqueId(),
      name: 'New Population',
      description: '',
      nextStep: '',
      groupOperatorAnd: true,
      blocks: [block]
    };

    this.population = newPopulation;
    this.populationCopy = this.clonePopulation(newPopulation);
    this.isNew = true;
    this.editMode = true;
  }

  private clonePopulation(population: Population): Population {
    return JSON.parse(JSON.stringify(population));
  }
}
